import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Sensors as SensorsIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Radar as RadarIcon,
  Public as PublicIcon,
  Psychology as AIIcon,
  Merge as MergeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const SensorIntegrationComponentDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ift-rvss-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to IFT/RVSS System"
        >
          Back to IFT/RVSS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Sensor Integration Suite
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Multi-Sensor Fusion and Detection Analytics Platform
          </Typography>
        </Box>
      </Box>

      {/* Component Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Component Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Sensor Integration Suite is the intelligence layer that combines data from multiple detection technologies 
            to create comprehensive situational awareness. By fusing inputs from ground sensors, radar systems, cameras, 
            and other sources, the suite uses advanced analytics and machine learning to distinguish between legitimate 
            activity (wildlife, weather) and actual threats (human crossings, vehicle movement). This multi-sensor approach 
            dramatically reduces false alarms while improving detection accuracy and providing actionable intelligence to 
            Border Patrol agents.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Component Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="Sensor Integration Suite 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is Sensor Integration Suite"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    An <strong>intelligent sensor fusion platform</strong> that integrates data from <strong>ground sensors 
                    (seismic, acoustic), radar systems, cameras, weather stations, and other detection technologies</strong>. 
                    Uses <strong>machine learning algorithms and AI-powered analytics</strong> to correlate sensor inputs, 
                    <strong>classify detected objects (human, vehicle, animal)</strong>, and <strong>filter false alarms</strong> 
                    to provide high-confidence alerts to operators.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses Sensor Integration Suite"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Managed by <strong>CBP Office of Technology Innovation and Acquisition (OTIA)</strong> and <strong>Border 
                    Patrol technology specialists</strong>. Primary beneficiaries are <strong>surveillance operators at sector 
                    operations centers, field agents responding to alerts, tactical coordinators, and intelligence analysts</strong>. 
                    System maintained by <strong>contractor technical teams and CBP engineers</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was Sensor Integration Suite deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial sensor fusion capabilities deployed in <strong>2010-2012</strong> with basic correlation algorithms. 
                    Major enhancement in <strong>2015-2017</strong> with machine learning integration. <strong>2019-2021</strong> 
                    deployment of AI-powered classification and false alarm reduction. <strong>2022-present</strong> continuous 
                    improvement with deep learning models. Operates <strong>24/7</strong> with <strong>real-time processing</strong> 
                    of sensor data streams.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is Sensor Integration Suite deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Processing infrastructure</strong> at sector operations centers and CBP data centers. <strong>Sensor 
                    networks</strong> deployed along U.S.-Mexico border in <strong>Arizona, Texas, California, and New Mexico</strong>. 
                    Integration with <strong>IFT towers, RVSS cameras, and ground sensor fields</strong>. Analytics engines process 
                    data from <strong>thousands of sensors across 9 Border Patrol sectors</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why Sensor Integration Suite exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To <strong>maximize detection accuracy while minimizing false alarms</strong> that waste agent resources. 
                    Single sensors produce high false alarm rates from <strong>wildlife, weather, and environmental factors</strong>. 
                    By <strong>fusing multiple sensor inputs and applying AI analytics</strong>, the suite achieves <strong>95%+ 
                    detection accuracy with 80% false alarm reduction</strong>. Critical for <strong>efficient resource allocation 
                    and agent response prioritization</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Sensor Types */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SensorsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Integrated Sensor Technologies
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SensorsIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Ground Sensors
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Buried and surface sensors for detecting ground disturbances.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Seismic Sensors:</strong> Detect footsteps and vehicle vibrations</Typography></li>
                <li><Typography variant="body2"><strong>Acoustic Sensors:</strong> Identify sounds of movement and voices</Typography></li>
                <li><Typography variant="body2"><strong>Magnetic Sensors:</strong> Detect metal objects and vehicles</Typography></li>
                <li><Typography variant="body2"><strong>Infrared Beams:</strong> Invisible trip-wire detection</Typography></li>
                <li><Typography variant="body2"><strong>Fiber Optic Cables:</strong> Buried detection along fence lines</Typography></li>
                <li><Typography variant="body2"><strong>Battery Life:</strong> 2-5 years autonomous operation</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RadarIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Radar Systems
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Radio frequency detection for all-weather surveillance.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Ground Surveillance Radar:</strong> Detects movement up to 7.5 miles</Typography></li>
                <li><Typography variant="body2"><strong>All-Weather Operation:</strong> Functions in fog, rain, dust, darkness</Typography></li>
                <li><Typography variant="body2"><strong>Doppler Processing:</strong> Distinguishes moving from stationary objects</Typography></li>
                <li><Typography variant="body2"><strong>Size Classification:</strong> Estimates target size (human vs vehicle)</Typography></li>
                <li><Typography variant="body2"><strong>Speed Measurement:</strong> Calculates target velocity</Typography></li>
                <li><Typography variant="body2"><strong>Multiple Target Tracking:</strong> Simultaneous tracking of 100+ objects</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AIIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  AI-Powered Analytics
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Machine learning for intelligent target classification.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Object Classification:</strong> Human, vehicle, animal identification</Typography></li>
                <li><Typography variant="body2"><strong>Behavioral Analysis:</strong> Pattern recognition for smuggling activity</Typography></li>
                <li><Typography variant="body2"><strong>False Alarm Filtering:</strong> Eliminates wildlife and weather triggers</Typography></li>
                <li><Typography variant="body2"><strong>Predictive Analytics:</strong> Anticipates likely crossing points</Typography></li>
                <li><Typography variant="body2"><strong>Deep Learning Models:</strong> Continuous improvement from data</Typography></li>
                <li><Typography variant="body2"><strong>Confidence Scoring:</strong> Alert priority based on certainty</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MergeIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Sensor Fusion Engine
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time correlation and integration of multiple sensor inputs.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Multi-Sensor Correlation:</strong> Combines data from all sensor types</Typography></li>
                <li><Typography variant="body2"><strong>Temporal Fusion:</strong> Tracks targets across time and sensors</Typography></li>
                <li><Typography variant="body2"><strong>Spatial Correlation:</strong> Geographic alignment of detections</Typography></li>
                <li><Typography variant="body2"><strong>Confidence Weighting:</strong> Prioritizes reliable sensor data</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Processing:</strong> Sub-second alert generation</Typography></li>
                <li><Typography variant="body2"><strong>Adaptive Algorithms:</strong> Adjusts to environmental conditions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Metrics */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Detection Performance
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Metric</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Single Sensor</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fused Sensors</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Improvement</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Detection Rate</strong></TableCell>
              <TableCell>85%</TableCell>
              <TableCell>95%+</TableCell>
              <TableCell>+10% improvement</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>False Alarm Rate</strong></TableCell>
              <TableCell>40%</TableCell>
              <TableCell>8%</TableCell>
              <TableCell>80% reduction</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Classification Accuracy</strong></TableCell>
              <TableCell>70%</TableCell>
              <TableCell>92%</TableCell>
              <TableCell>+22% improvement</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Response Time</strong></TableCell>
              <TableCell>5-10 minutes</TableCell>
              <TableCell>2-3 minutes</TableCell>
              <TableCell>60% faster</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Coverage Efficiency</strong></TableCell>
              <TableCell>60%</TableCell>
              <TableCell>90%</TableCell>
              <TableCell>+30% improvement</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Agent Utilization</strong></TableCell>
              <TableCell>50% wasted</TableCell>
              <TableCell>90% effective</TableCell>
              <TableCell>80% improvement</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Capacity
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                10K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ground Sensors
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Deployed sensors
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                95%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Detection Rate
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                With sensor fusion
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                80%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                False Alarm Reduction
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                AI filtering
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                &lt;1sec
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Processing Time
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Real-time alerts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Operational Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Higher Accuracy:</strong> 95%+ detection with multi-sensor fusion</Typography></li>
                <li><Typography variant="body2"><strong>Fewer False Alarms:</strong> 80% reduction saves agent time</Typography></li>
                <li><Typography variant="body2"><strong>Better Classification:</strong> Distinguish humans, vehicles, animals</Typography></li>
                <li><Typography variant="body2"><strong>Faster Response:</strong> High-confidence alerts enable rapid deployment</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Resource Optimization:</strong> Focus agents on real threats</Typography></li>
                <li><Typography variant="body2"><strong>All-Weather Detection:</strong> Radar works in any conditions</Typography></li>
                <li><Typography variant="body2"><strong>Continuous Learning:</strong> AI improves over time</Typography></li>
                <li><Typography variant="body2"><strong>Cost Effective:</strong> Maximize existing sensor investments</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="IFT Towers" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Fixed tower sensors</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="RVSS Cameras" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Video surveillance</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Ground Sensor Fields" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Seismic/acoustic networks</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Radar Networks" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Ground surveillance radar</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Operations Centers" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Alert distribution</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="GIS Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Geographic mapping</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Weather Stations" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Environmental data</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Agent Devices" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Mobile alerts</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SensorIntegrationComponentDetail;
